import React, { useEffect, useState } from "react";
import {
  TrendingUp,
  HardHat,
  BarChart3,
  Landmark,
  Building2,
  Users,
} from "lucide-react";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { Bar } from "react-chartjs-2";
import {
  Chart,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { baseApiService } from "../../shared/services/api/axios-config/axios-config";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { Card, CardContent, Badge, ProgressBar, Spinner } from "../../shared/components/ui";

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);


type Work = {
  id: string;
  workName: string;
  status: string;
  percentageOfWork: number;
};

type ExpenseCategory = {
  chartOfAccounts: string;
  accountType: string;
  totalExpenses: number;
};

type DashboardData = {
  totalEmployeesActive: number;
  monthlyBilling: number;
  annualRevenue: number;
  annualdistributionOfExpenses: ExpenseCategory[];
  worksInProgress: number;
  works: Work[];
  annualProfitMargin: number;
  accountsPayableDay: number; // quantidade de contas
  accountsReceivableDay: number; // quantidade de contas
};

export function PageDeshboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);
  const [ano, setAno] = useState(() => new Date().getFullYear());

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const response = await baseApiService.get(`/dashboard/${ano}`);
        setData(response.data.data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [ano]);

  if (loading) {
    return (
      <BaseLayout
        title="Dashboard"
        barraDeFerramentas={
          <FerramanetasDaListagem
            mostraBotaoNovo={false}
            mostrarSeletorAno
            anoSelecionado={ano}
            aoMudarAno={setAno}
          />
        }
      >
        <div className="flex min-h-[60vh] items-center justify-center">
          <Spinner size={40} />
        </div>
      </BaseLayout>
    );
  }

  if (!data) {
    return (
      <BaseLayout
        title="Dashboard"
        barraDeFerramentas={
          <FerramanetasDaListagem
            mostraBotaoNovo={false}
            mostrarSeletorAno
            anoSelecionado={ano}
            aoMudarAno={setAno}
          />
        }
      >
        <div className="flex min-h-[60vh] items-center justify-center">
          <p className="text-red-600">Erro ao carregar dados do dashboard.</p>
        </div>
      </BaseLayout>
    );
  }

  // KPIs
  const kpis = [
    {
      title: "Faturamento Mensal",
      value: Number(data.monthlyBilling ?? 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      }),
      icon: <Landmark className="h-5 w-5" />,
      isPositive: Number(data.monthlyBilling ?? 0) >= 0,
    },
    {
      title: "Obras Ativas",
      value: Number(data.worksInProgress ?? 0),
      icon: <Building2 className="h-5 w-5" />,
      isPositive: true,
    },
    {
      title: "Funcionários Ativos",
      value: Number(data.totalEmployeesActive ?? 0),
      icon: <Users className="h-5 w-5" />,
      isPositive: true,
    },
    {
      title: "Margem de Lucro Anual",
      value: `${Number(data.annualProfitMargin ?? 0).toFixed(2)}%`,
      icon: <TrendingUp className="h-5 w-5" />,
      isPositive: Number(data.annualProfitMargin ?? 0) >= 0,
    },
  ];

  function getDynamicColors(length: number) {
    const palette = [
      "#2196F3",
      "#4CAF50",
      "#FF9800",
      "#9C27B0",
      "#E91E63",
      "#00BCD4",
      "#FFC107",
      "#8BC34A",
    ];
    return Array.from({ length }, (_, i) => palette[i % palette.length]);
  }

  // Gráfico de Barras - Distribuição de Gastos
  const barData = {
    labels: data.annualdistributionOfExpenses.map(
      (item) => item.chartOfAccounts
    ),
    datasets: [
      {
        label: "Total de Gastos (R$)",
        data: data.annualdistributionOfExpenses.map(
          (item) => item.totalExpenses
        ),
        backgroundColor: getDynamicColors(
          data.annualdistributionOfExpenses.length
        ),
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) =>
            `R$ ${Number(context.parsed.y).toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
            })}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (tickValue: string | number) =>
            `R$ ${Number(tickValue).toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
            })}`,
        },
      },
    },
  };

  // Lista detalhada de gastos
  const gastosDetalhados = data.annualdistributionOfExpenses.map(
    (item, idx) => ({
      ...item,
      color:
        barData.datasets[0].backgroundColor[
          idx % barData.datasets[0].backgroundColor.length
        ],
    })
  );

  // Obras
  const obras = data.works.map((obra) => ({
    nome: obra.workName,
    progresso: obra.percentageOfWork,
    status:
      obra.percentageOfWork === 100
        ? "Finalizada"
        : obra.percentageOfWork === 0
        ? "Início"
        : "Em andamento",
  }));

  return (
    <BaseLayout
      title="Dashboard"
      barraDeFerramentas={
        <FerramanetasDaListagem
          mostraBotaoNovo={false}
          mostrarSeletorAno
          anoSelecionado={ano}
          aoMudarAno={setAno}
        />
      }
    >
      <div className="space-y-4 sm:space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          {kpis.map((kpi, index) => (
            <Card key={index}>
              <CardContent className="flex flex-col items-center gap-3 text-center sm:flex-row sm:items-start sm:text-left">
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10 ${
                    kpi.isPositive
                      ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300"
                      : "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300"
                  }`}
                >
                  {kpi.icon}
                </div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-slate-900 sm:text-2xl dark:text-white">
                    {kpi.value}
                  </div>
                  <div className="text-xs text-slate-500 sm:text-sm dark:text-slate-400">
                    {kpi.title}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Progresso das Obras */}
        <Card>
          <CardContent>
            <h2 className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-900 sm:text-lg dark:text-white">
              <HardHat className="h-5 w-5 text-primary-600" />
              Progresso das Obras
            </h2>
            <div className="space-y-4">
              {obras.map((obra, index) => (
                <div key={index}>
                  <div className="mb-1 flex flex-col justify-between gap-0.5 sm:flex-row">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {obra.nome}
                    </span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {obra.progresso}%
                    </span>
                  </div>
                  <ProgressBar value={obra.progresso} className="mb-2" />
                  <Badge color="neutral">{obra.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Distribuição de Gastos Anual */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <Card className="lg:col-span-7">
            <CardContent className="flex h-full flex-col items-center justify-center">
              <h2 className="mb-4 flex w-full items-center gap-2 text-base font-semibold text-slate-900 sm:text-lg dark:text-white">
                <BarChart3 className="h-5 w-5 text-primary-600" />
                Distribuição de Gastos Anual (Gráfico)
              </h2>
              <div className="mx-auto mt-2 w-full max-w-xl">
                <Bar data={barData} options={barOptions} />
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-5">
            <CardContent className="flex h-full flex-col">
              <h2 className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-900 sm:text-lg dark:text-white">
                <BarChart3 className="h-5 w-5 text-primary-600" />
                Detalhamento de Gastos
              </h2>
              <div className="custom-scrollbar max-h-[250px] flex-1 space-y-2 overflow-y-auto pr-1 md:max-h-[350px]">
                {gastosDetalhados.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span
                      className="h-3.5 w-3.5 shrink-0 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <div className="flex w-full items-center justify-between gap-2 text-sm">
                      <span className="truncate text-slate-700 dark:text-slate-200">
                        {item.chartOfAccounts}
                      </span>
                      <span className="shrink-0 whitespace-nowrap text-right font-medium text-slate-900 dark:text-white">
                        {Number(item.totalExpenses).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Resumo Financeiro */}
        <div className="grid grid-cols-1 gap-4 pb-4 sm:grid-cols-3">
          <Card>
            <CardContent className="text-center">
              <p className="mb-1 text-sm font-semibold text-emerald-600 sm:text-base">
                Receita Anual
              </p>
              <p className="text-xl font-bold text-slate-900 sm:text-2xl dark:text-white">
                {Number(data.annualRevenue ?? 0).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </p>
              <p className="text-xs text-slate-500 sm:text-sm dark:text-slate-400">
                Este ano
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="text-center">
              <p className="mb-1 text-sm font-semibold text-amber-600 sm:text-base">
                Contas a Pagar (Hoje)
              </p>
              <p className="text-xl font-bold text-slate-900 sm:text-2xl dark:text-white">
                {Number(data.accountsPayableDay ?? 0)}
              </p>
              <p className="text-xs text-slate-500 sm:text-sm dark:text-slate-400">
                Quantidade de contas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="text-center">
              <p className="mb-1 text-sm font-semibold text-primary-600 sm:text-base">
                Contas a Receber (Hoje)
              </p>
              <p className="text-xl font-bold text-emerald-600 sm:text-2xl">
                {Number(data.accountsReceivableDay ?? 0)}
              </p>
              <p className="text-xs text-slate-500 sm:text-sm dark:text-slate-400">
                Quantidade de contas
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </BaseLayout>
  );
}
